import { createApi } from '@reduxjs/toolkit/query/react'
import { hcPortalBaseQuery } from '@/features/baseQuery.ts'

import {
  AddTimeOffRequestOnBehalfRequest,
  AddTimeOffRequestRequest,
  AddTimeOffPolicyRequest,
  AddTimeOffTypeRequest,
  ApproveTimeOffRequestRequest,
  BulkApproveTimeOffRequestsRequest,
  CancelTimeOffRequestRequest,
  DownloadTimeOffReportRequest,
  EditTimeOffPolicyRequest,
  EditTimeOffTypeRequest,
  ExportCalendarConfigRequest,
  GetCountryTimeOffConfigRequest,
  GetCountryTimeOffPoliciesRequest,
  GetGrantTimeOffBalanceCandidatesRequest,
  GetManagedTimeOffRequestsRequest,
  GetOwnTimeOffRequestsRequest,
  GetTimeOffBalanceHistoriesRequest,
  GetTimeOffBalanceRequest,
  GetTimeOffEntitlementRequest,
  GetTimeOffRequestDetailsRequest,
  GetTimeOffRequestsInTeamRequest,
  GetTimeOffRequestsRequest,
  GrantTimeOffBalanceRequest,
  HandleTimeOffAllocationExpiryRequest,
  HandleTimeOffOffboardingRequest,
  HandleTimeOffOnboardingRequest,
  PopulateTimeOffBalanceRequest,
  ProcessAnnualTimeOffRolloverRequest,
  ReassignTimeOffPolicyRequest,
  RejectTimeOffRequestRequest,
  ReplaceCalendarRequest,
  SetClockRequest,
  UpdateCalendarConfigRequest,
  UpdateTimeOffAllocationRequest,
  UpdateTimeOffSupportingDocumentsRequest,
} from '@/features/time-off/time-off-request.def.ts'
import {
  CountryTimeOffConfig,
  GetTimeOffBalanceResponse,
  TimeOffBalance,
  TimeOffBalanceHistory,
  TimeOffOnboardingPreviewResult,
  TimeOffPolicy,
  TimeOffRequest,
  TimeOffSummariesResponse,
} from '@/features/time-off/time-off.def.ts'
import { EmployeeEligibleOption } from '@/features/employee-data/employee-data.def.ts'
import { HcError } from '@/common/types/api.def.ts'
import { downloadExcel, downloadFile } from '@/utils/misc.ts'

export const timeOffApi = createApi({
  reducerPath: 'timeOff',
  baseQuery: hcPortalBaseQuery(),
  tagTypes: ['TimeOffRequest', 'TimeOffConfig', 'TimeOffBalance', 'TimeOffBalanceHistory', 'GrantTimeOffBalanceCandidate', 'TimeOffClock', 'TimeOffEntitlement'],
  endpoints: (builder) => ({
    getCountryTimeOffConfig: builder.query<CountryTimeOffConfig, GetCountryTimeOffConfigRequest>({
      query: (request) => ({
        url: '/TimeOffConfig/GetCountryTimeOffConfig',
        method: 'GET',
        cache: 'no-cache',
        params: request,
      }),
      providesTags: (_result, _error, arg) => [
        { type: 'TimeOffConfig', id: arg.country },
      ],
    }),
    getCountryTimeOffPolicies: builder.query<TimeOffPolicy[], GetCountryTimeOffPoliciesRequest>({
      query: (request) => ({
        url: '/TimeOffConfig/GetCountryTimeOffPolicies',
        method: 'GET',
        cache: 'no-cache',
        params: request,
      }),
      providesTags: (_result, _error, arg) => [
        { type: 'TimeOffConfig', id: arg.country },
      ],
    }),
    addTimeOffPolicy: builder.mutation<TimeOffPolicy, AddTimeOffPolicyRequest>({
      query: (request) => ({
        url: '/TimeOffConfig/AddTimeOffPolicy',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'TimeOffConfig', id: arg.country },
      ],
    }),
    editTimeOffPolicy: builder.mutation<boolean, EditTimeOffPolicyRequest>({
      query: (request) => ({
        url: '/TimeOffConfig/EditTimeOffPolicy',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'TimeOffConfig', id: arg.country },
      ],
    }),
    reassignTimeOffPolicy: builder.mutation<boolean, ReassignTimeOffPolicyRequest>({
      query: (request) => ({
        url: '/TimeOffConfig/ReassignTimeOffPolicy',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'TimeOffBalance', id: arg.employeeId },
        { type: 'TimeOffBalanceHistory', id: arg.employeeId },
        { type: 'TimeOffRequest', id: arg.employeeId },
      ],
    }),
    updateCalendarConfig: builder.mutation<boolean, UpdateCalendarConfigRequest>({
      query: (request) => ({
        url: '/TimeOffConfig/UpdateCalendarConfig',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'TimeOffConfig', id: arg.country },
      ],
    }),
    addTimeOffType: builder.mutation<boolean, AddTimeOffTypeRequest>({
      query: (request) => ({
        url: '/TimeOffConfig/AddTimeOffType',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'TimeOffConfig', id: arg.country },
      ],
    }),
    editTimeOffType: builder.mutation<boolean, EditTimeOffTypeRequest>({
      query: (request) => ({
        url: '/TimeOffConfig/EditTimeOffType',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'TimeOffConfig', id: arg.country },
      ],
    }),
    getOwnTimeOffRequests: builder.query<TimeOffRequest[], GetOwnTimeOffRequestsRequest>({
      query: (request) => ({
        url: '/TimeOff/GetOwnTimeOffRequests',
        method: 'GET',
        cache: 'no-cache',
        params: request,
      }),
      providesTags: [{ type: 'TimeOffRequest', id: 'OWN' }],
    }),
    getTimeOffRequests: builder.query<TimeOffRequest[], GetTimeOffRequestsRequest>({
      query: (request) => ({
        url: '/TimeOff/GetTimeOffRequests',
        method: 'GET',
        cache: 'no-cache',
        params: request,
      }),
      providesTags: (_result, _error, arg) => [{ type: 'TimeOffRequest', id: arg.employeeId }],
    }),
    getTimeOffBalanceHistories: builder.query<TimeOffBalanceHistory[], GetTimeOffBalanceHistoriesRequest>({
      query: (request) => ({
        url: '/TimeOff/GetTimeOffBalanceHistories',
        method: 'GET',
        cache: 'no-cache',
        params: request,
      }),
      providesTags: (_result, _error, arg) => [{ type: 'TimeOffBalanceHistory', id: arg.employeeId }],
    }),
    getManagedTimeOffRequests: builder.query<TimeOffRequest[], GetManagedTimeOffRequestsRequest>({
      query: (request) => ({
        url: '/TimeOff/GetManagedTimeOffRequests',
        method: 'GET',
        cache: 'no-cache',
        params: request,
      }),
      providesTags: [{ type: 'TimeOffRequest', id: 'MANAGED' }],
    }),
    getTimeOffRequestDetails: builder.query<TimeOffRequest, GetTimeOffRequestDetailsRequest>({
      query: (request) => ({
        url: '/TimeOff/GetTimeOffRequestDetails',
        method: 'GET',
        cache: 'no-cache',
        params: request,
      }),
      providesTags: (_result, _error, request) => {
        return [{ type: 'TimeOffRequest', id: request.timeOffRequestId }]
      },
    }),
    addTimeOffRequest: builder.mutation<boolean, AddTimeOffRequestRequest>({
      query: (request) => ({
        url: '/TimeOff/AddTimeOffRequest',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'TimeOffRequest', id: 'OWN' },
        { type: 'TimeOffBalance', id: 'OWN' },
        { type: 'TimeOffBalanceHistory', id: 'OWN' },
        { type: 'TimeOffRequest', id: arg.employeeId },
        { type: 'TimeOffBalance', id: arg.employeeId },
        { type: 'TimeOffBalanceHistory', id: arg.employeeId },
      ],
    }),
    addTimeOffRequestOnBehalf: builder.mutation<boolean, AddTimeOffRequestOnBehalfRequest>({
      query: (request) => ({
        url: '/TimeOff/AddTimeOffRequestOnBehalf',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'TimeOffRequest', id: arg.targetEmployeeId },
        { type: 'TimeOffBalance', id: arg.targetEmployeeId },
        { type: 'TimeOffBalanceHistory', id: arg.targetEmployeeId },
      ],
    }),
    approveTimeOffRequest: builder.mutation<boolean, ApproveTimeOffRequestRequest>({
      query: (request) => ({
        url: '/TimeOff/ApproveTimeOffRequest',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'TimeOffRequest', id: request.id }]
      },
    }),
    bulkApproveTimeOffRequests: builder.mutation<boolean, BulkApproveTimeOffRequestsRequest>({
      query: (request) => ({
        url: '/TimeOff/BulkApproveTimeOffRequests',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: 'TimeOffRequest', id: 'MANAGED' },
        ...(request.timeOffRequestIds.map(id => {
          return {
            type: 'TimeOffRequest' as const,
            id: `${id}`,
          }
        }))],
    }),
    rejectTimeOffRequest: builder.mutation<boolean, RejectTimeOffRequestRequest>({
      query: (request) => ({
        url: '/TimeOff/RejectTimeOffRequest',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'TimeOffRequest', id: request.id }]
      },
    }),
    cancelTimeOffRequest: builder.mutation<boolean, CancelTimeOffRequestRequest>({
      query: (request) => ({
        url: '/TimeOff/CancelTimeOffRequest',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'TimeOffRequest', id: 'OWN' },
        { type: 'TimeOffBalance', id: 'OWN' },
        { type: 'TimeOffBalanceHistory', id: 'OWN' },
        { type: 'TimeOffRequest', id: arg.employeeId },
        { type: 'TimeOffBalance', id: arg.employeeId },
        { type: 'TimeOffBalanceHistory', id: arg.employeeId },
      ],
    }),
    populateTimeOffBalance: builder.mutation<boolean, PopulateTimeOffBalanceRequest>({
      query: (request) => ({
        url: '/TimeOffConfig/PopulateTimeOffBalance',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
    }),
    getOwnTimeOffBalance: builder.query<TimeOffBalance[], void>({
      query: () => ({
        url: '/TimeOff/GetOwnTimeOffBalance',
        method: 'GET',
        cache: 'no-cache',
      }),
      providesTags: () => {
        return [{ type: 'TimeOffBalance', id: 'OWN' }]
      },
    }),
    getTimeOffBalance: builder.query<GetTimeOffBalanceResponse, GetTimeOffBalanceRequest>({
      query: (request) => ({
        url: '/TimeOff/GetTimeOffBalance',
        method: 'GET',
        cache: 'no-cache',
        params: request,
      }),
      providesTags: (_result, _error, request) => {
        return [{ type: 'TimeOffBalance', id: request.employeeId }]
      },
    }),
    grantTimeOffBalance: builder.mutation<TimeOffRequest[], GrantTimeOffBalanceRequest>({
      query: (request) => ({
        url: '/TimeOff/GrantTimeOffBalance',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
    }),
    getGrantTimeOffBalanceCandidates: builder.query<EmployeeEligibleOption[], GetGrantTimeOffBalanceCandidatesRequest>({
      query: (request) => ({
        url: '/TimeOffConfig/GetGrantTimeOffBalanceCandidates',
        method: 'GET',
        cache: 'no-cache',
        params: request,
      }),
      providesTags: (_result, _error, request) => {
        return [{ type: 'GrantTimeOffBalanceCandidate', id: request.country }]
      },
    }),
    getTimeOffEntitlement: builder.query<TimeOffOnboardingPreviewResult, GetTimeOffEntitlementRequest>({
      query: (request) => ({
        url: '/TimeOffConfig/GetTimeOffEntitlement',
        method: 'GET',
        cache: 'no-cache',
        params: request,
      }),
    }),
    updateTimeOffAllocation: builder.mutation<boolean, UpdateTimeOffAllocationRequest>({
      query: (request) => ({
        url: '/TimeOff/UpdateTimeOffAllocation',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'TimeOffBalance', id: request.employeeId }]
      },
    }),
    updateTimeOffSupportingDocuments: builder.mutation<boolean, UpdateTimeOffSupportingDocumentsRequest>({
      query: (request) => ({
        url: '/TimeOff/UpdateTimeOffSupportingDocuments',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'TimeOffRequest', id: request.id }]
      },
    }),
    downloadTimeOffReport: builder.mutation<void, DownloadTimeOffReportRequest>({
      query: (request) => {
        return {
          url: '/TimeOffConfig/DownloadTimeOffReport',
          method: 'POST',
          cache: 'no-cache',
          body: request,
          responseHandler: async (response) => {
            if (!response.ok) {
              return await response.json() as HcError
            }
            const blob = await response.blob()
            downloadFile(`Time Off Report.xlsx`, blob)
          },
        }
      },
    }),
    previewTimeOffOnboarding: builder.query<TimeOffOnboardingPreviewResult, HandleTimeOffOnboardingRequest>({
      query: (request) => ({
        url: '/TimeOffConfig/PreviewTimeOffOnboarding',
        method: 'GET',
        cache: 'no-cache',
        params: request,
      }),
    }),
    handleTimeOffOnboarding: builder.mutation<boolean, HandleTimeOffOnboardingRequest>({
      query: (request) => ({
        url: '/TimeOffConfig/HandleTimeOffOnboarding',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'TimeOffBalance', id: request.employeeId }]
      },
    }),
    handleTimeOffOffboarding: builder.mutation<boolean, HandleTimeOffOffboardingRequest>({
      query: (request) => ({
        url: '/TimeOffConfig/HandleTimeOffOffboarding',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, arg) => {
        return [
          { type: 'TimeOffRequest', id: arg.employeeId },
          { type: 'TimeOffBalance', id: arg.employeeId },
          { type: 'TimeOffBalanceHistory', id: arg.employeeId },
        ]
      },
    }),
    getTimeOffClockOffset: builder.query<number, void>({
      query: () => ({
        url: '/TimeOffClock/GetOffset',
        method: 'GET',
        cache: 'no-cache',
      }),
      providesTags: ['TimeOffClock'],
    }),
    setTimeOffClock: builder.mutation<boolean, SetClockRequest>({
      query: (request) => ({
        url: '/TimeOffClock/SetClock',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: ['TimeOffClock'],
    }),
    clearTimeOffClock: builder.mutation<boolean, void>({
      query: () => ({
        url: '/TimeOffClock/ClearClock',
        method: 'POST',
        cache: 'no-cache',
      }),
      invalidatesTags: ['TimeOffClock'],
    }),
    triggerRollover: builder.mutation<boolean, ProcessAnnualTimeOffRolloverRequest>({
      query: (request) => ({
        url: '/TimeOffClock/TriggerRollover',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
    }),
    triggerExpiry: builder.mutation<boolean, HandleTimeOffAllocationExpiryRequest>({
      query: (request) => ({
        url: '/TimeOffClock/TriggerExpiry',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
    }),
    replaceCalendar: builder.mutation<void, ReplaceCalendarRequest>({
      query: (request) => {
        const formData = new FormData()
        formData.append('excelFile', request.excelFile)
        formData.append('year', request.year.toString())
        formData.append('country', request.country)

        return {
          url: '/TimeOffConfig/ReplaceCalendar',
          method: 'POST',
          cache: 'no-cache',
          body: formData,
        }
      },
      invalidatesTags: (_result, _error, arg) => [
        { type: 'TimeOffConfig', id: arg.country },
      ],
    }),
    exportCalendarConfig: builder.mutation<void, ExportCalendarConfigRequest>({
      query: (request) => {
        return {
          url: '/TimeOffConfig/ExportCalendarConfig',
          method: 'POST',
          cache: 'no-cache',
          body: request,
          responseHandler: async (response) => {
            if (!response.ok) {
              return await response.json() as HcError
            }
            const blob = await response.blob()
            downloadExcel(`Public Holidays - ${request.year} - ${request.country}`, blob)
          },
        }
      },
    }),
    getTimeOffRequestsInTeam: builder.query<TimeOffSummariesResponse, GetTimeOffRequestsInTeamRequest>({
      query: (request) => ({
        url: '/TimeOff/GetTimeOffRequestsInTeam',
        method: 'GET',
        cache: 'no-cache',
        params: request,
      }),
    }),
  }),
})

export const {
  useGetCountryTimeOffConfigQuery,
  useGetCountryTimeOffPoliciesQuery,
  useAddTimeOffPolicyMutation,
  useEditTimeOffPolicyMutation,
  useReassignTimeOffPolicyMutation,
  useUpdateCalendarConfigMutation,
  useAddTimeOffTypeMutation,
  useEditTimeOffTypeMutation,
  useGetTimeOffRequestsQuery,
  useGetManagedTimeOffRequestsQuery,
  useGetTimeOffRequestDetailsQuery,
  useAddTimeOffRequestMutation,
  useAddTimeOffRequestOnBehalfMutation,
  useGetTimeOffBalanceQuery,
  useApproveTimeOffRequestMutation,
  useBulkApproveTimeOffRequestsMutation,
  useRejectTimeOffRequestMutation,
  useCancelTimeOffRequestMutation,
  useGetTimeOffBalanceHistoriesQuery,
  useGrantTimeOffBalanceMutation,
  useGetGrantTimeOffBalanceCandidatesQuery,
  useGetTimeOffEntitlementQuery,
  useUpdateTimeOffAllocationMutation,
  useUpdateTimeOffSupportingDocumentsMutation,
  useDownloadTimeOffReportMutation,
  usePreviewTimeOffOnboardingQuery,
  useHandleTimeOffOnboardingMutation,
  useHandleTimeOffOffboardingMutation,
  useGetTimeOffClockOffsetQuery,
  useSetTimeOffClockMutation,
  useClearTimeOffClockMutation,
  useTriggerRolloverMutation,
  useTriggerExpiryMutation,
  useReplaceCalendarMutation,
  useExportCalendarConfigMutation,
  useGetTimeOffRequestsInTeamQuery,
} = timeOffApi